import { useCallback, useEffect, useRef, useState } from "react";
import {
  clearChatHistory,
  fetchChatHistory,
  fetchExamples,
  streamAnswer,
  type ChatEvent,
} from "../api/chat";
import { formatError } from "../utils/formatError";
import type { DomainResolution, SearchResponse } from "../api/types";

/**
 * One turn on screen. A user turn is only text; an assistant turn also carries what the tools
 * returned, so the results a card shows come from the corpus rather than from the answer's prose.
 */
export interface ChatTurn {
  id: number;
  role: "user" | "assistant";
  text: string;
  /** `search_files` payloads, in call order. Each renders as a result group. */
  searches: SearchResponse[];
  /** `resolve_domain` payloads that were genuinely ambiguous. */
  domains: DomainResolution[];
  /** What the agent is doing right now, e.g. "search_files". Empty once the turn is finished. */
  activity: string;
  /** Set when the turn failed. Rendered in place of an answer, never silently swallowed. */
  error: string;
  /** True while the answer is still arriving. */
  pending: boolean;
}

const TRUNCATED_NOTICE = "This answer stopped early. Try narrowing the request.";

export interface ChatController {
  turns: ChatTurn[];
  suggestions: string[];
  examples: string[];
  busy: boolean;
  /** Ask a question. Ignored while a previous one is still being answered. */
  ask: (message: string) => void;
  /** Stop the answer in flight. What has already arrived stays on screen. */
  stop: () => void;
  /** Forget the conversation, here and on the server. */
  reset: () => Promise<void>;
}

/**
 * Owns one conversation: the turns on screen, the stream feeding them, and the chips beneath.
 *
 * Screens render; this decides. Kept as a hook rather than inside `ChatScreen` so the streaming
 * state machine can be reasoned about — and replaced — without touching any markup.
 */
export function useChat(): ChatController {
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [examples, setExamples] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const nextId = useRef(0);
  const abort = useRef<AbortController | null>(null);

  const patch = useCallback((change: (turn: ChatTurn) => ChatTurn) => {
    // Every event lands on the newest turn, which is the assistant turn this run created.
    setTurns((current) =>
      current.map((turn, index) => (index === current.length - 1 ? change(turn) : turn)),
    );
  }, []);

  const append = useCallback((role: ChatTurn["role"], text: string, pending = false): void => {
    const turn: ChatTurn = {
      id: nextId.current++,
      role,
      text,
      searches: [],
      domains: [],
      activity: "",
      error: "",
      pending,
    };
    setTurns((current) => [...current, turn]);
  }, []);

  const ask = useCallback(
    (message: string): void => {
      const question = message.trim();
      if (!question || busy) {
        return;
      }
      setSuggestions([]);
      append("user", question);
      append("assistant", "", true);
      setBusy(true);
      const controller = new AbortController();
      abort.current = controller;
      void runStream(question, controller, patch, setSuggestions).finally(() => {
        abort.current = null;
        setBusy(false);
      });
    },
    [append, busy, patch],
  );

  const stop = useCallback((): void => {
    abort.current?.abort();
    patch((turn) => ({ ...turn, pending: false, activity: "" }));
  }, [patch]);

  const reset = useCallback(async (): Promise<void> => {
    abort.current?.abort();
    await clearChatHistory();
    setTurns([]);
    setSuggestions([]);
  }, []);

  useEffect(() => {
    // Restore the thread and the starter questions once, on arrival. Neither is worth an error
    // banner: a fresh conversation and no example chips are both perfectly usable states.
    void (async () => {
      const [restored, starters] = await Promise.all([
        fetchChatHistory().catch(() => []),
        fetchExamples().catch(() => []),
      ]);
      setExamples(starters);
      setTurns(
        restored.map((message) => ({
          id: nextId.current++,
          role: message.role,
          text: message.text,
          searches: [],
          domains: [],
          activity: "",
          error: "",
          pending: false,
        })),
      );
    })();
  }, []);

  return { turns, suggestions, examples, busy, ask, stop, reset };
}

/** Drive one answer to completion, folding each event into the pending turn. */
async function runStream(
  question: string,
  controller: AbortController,
  patch: (change: (turn: ChatTurn) => ChatTurn) => void,
  setSuggestions: (items: string[]) => void,
): Promise<void> {
  try {
    await streamAnswer(question, (event) => apply(event, patch, setSuggestions), controller.signal);
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      return;
    }
    patch((turn) => ({ ...turn, pending: false, activity: "", error: formatError(error) }));
  }
}

/** Fold one stream event into the turn being answered. */
function apply(
  event: ChatEvent,
  patch: (change: (turn: ChatTurn) => ChatTurn) => void,
  setSuggestions: (items: string[]) => void,
): void {
  switch (event.type) {
    case "delta":
      return patch((turn) => ({ ...turn, text: turn.text + event.text, activity: "" }));
    case "tool":
      return patch((turn) => ({ ...turn, activity: event.name }));
    case "search":
      return patch((turn) => ({ ...turn, searches: [...turn.searches, event.payload] }));
    case "domains":
      return patch((turn) => ({ ...turn, domains: [...turn.domains, event.payload] }));
    case "suggestions":
      return setSuggestions(event.items);
    case "done":
      return patch((turn) => finish(turn, event.reply, event.hit_turn_limit));
    case "error":
      return patch((turn) => ({ ...turn, pending: false, activity: "", error: event.message }));
  }
}

/** Close a turn out, taking the final reply when nothing was streamed (a run that stopped early). */
function finish(turn: ChatTurn, reply: string, hitTurnLimit: boolean): ChatTurn {
  return {
    ...turn,
    text: turn.text || reply,
    error: hitTurnLimit && !turn.text ? TRUNCATED_NOTICE : turn.error,
    pending: false,
    activity: "",
  };
}
